import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Label } from '@shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { Spinner } from '@shared/components/ui/Spinner';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { BatchApiAdapter } from '@modules/Batches/adapters/BatchApiAdapter';
import { getBatchTotalStock, type Batch } from '@modules/Batches/models/Batch';

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  active: { bg: 'bg-bg-success', text: 'text-success' },
  expired: { bg: 'bg-bg-error', text: 'text-error' },
  depleted: { bg: 'bg-card-border light:bg-bg-warning', text: 'text-warning' },
  blocked: { bg: 'bg-card-border light:bg-bg-info', text: 'text-info' },
};

const getBatchStatus = (batch: Batch): string => {
  if (batch.blocked_at && !batch.unblocked_at) return 'blocked';
  if (batch.expiry_date && new Date(batch.expiry_date) < new Date()) return 'expired';
  const totalStock = getBatchTotalStock(batch);
  if (totalStock === 0) return 'depleted';
  return 'active';
};

const formatDate = (date: string | null): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

interface ProductBatchesProps {
  productId: string | number;
  /** Si false, no se pide a la API hasta abrir la pestaña (listado con paneles que no se desmontan) */
  queryEnabled?: boolean;
  batchActive: boolean;
  setBatchActive: (value: boolean) => void;
  batchType: string;
  setBatchType: (value: string) => void;
  /** Solo tenant_3pl puede cambiar uso de lotes y tipo de rotación */
  canEditBatchSettings: boolean;
  /** Flujo especial para activar lotes en productos con movimientos de stock */
  canActivateBatches?: boolean;
  hasStockMovements?: boolean;
  hasProductStock?: boolean;
  onOpenActivateBatches?: () => void;
}

export const ProductBatches = ({
  productId,
  queryEnabled = true,
  batchActive,
  setBatchActive,
  batchType,
  setBatchType,
  canEditBatchSettings,
  canActivateBatches = false,
  hasStockMovements = false,
  hasProductStock = false,
  onOpenActivateBatches,
}: ProductBatchesProps) => {
  const { t } = useTranslation('createEditProductsModule');
  const { t: tBatches } = useTranslation('batchesModule');
  const navigate = useNavigate();

  const httpClient = createHttpClient(apiClientWH);
  const adapter = new BatchApiAdapter(httpClient);

  const { data: batches = [], isLoading } = useQuery({
    queryKey: ['product-batches', productId],
    queryFn: () => adapter.getProductBatches(Number(productId)),
    enabled: !!productId && queryEnabled,
    staleTime: 2 * 60 * 1000,
  });

  const showBatchConfig = (import.meta.env.VITE_ENABLE_BATCH_CONFIG ?? 'true') !== 'false';
  const showActivateSection = canActivateBatches && !batchActive && hasStockMovements;
  const showStandardBatchConfig = showBatchConfig && !showActivateSection;

  return (
    <div className="space-y-8 pt-4">
      {showActivateSection && (
        <div
          className="rounded-lg border border-card-border bg-card-bg p-5 space-y-3"
          data-testid="activate-batches-section"
        >
          <div>
            <h3 className="font-semibold text-base">{t('activate_batches.section_title')}</h3>
            <p className="text-sm text-complementary-text mt-1">
              {hasProductStock
                ? t('activate_batches.section_desc_with_stock')
                : t('activate_batches.section_desc_no_stock')}
            </p>
          </div>
          <Button variant="primary" onClick={onOpenActivateBatches} data-testid="open-activate-batches-button">
            {t('activate_batches.open_button')}
          </Button>
        </div>
      )}

      {showStandardBatchConfig && (
        <div className="space-y-4 gap-4 grid grid-cols-1 md:grid-cols-6 pb-6 border-b border-card-border">
          <div className="col-span-1 md:col-span-2">
            <h2 className="font-semibold text-lg mb-1">{t('sections.batch_title')}</h2>
            <p className="text-sm">{t('sections.batch_desc')}</p>
          </div>
          <div className="col-span-1 md:col-span-4 !mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <div className="flex flex-col gap-3">
                <Label>{t('form.batch_active_label')}</Label>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={batchActive}
                    onCheckedChange={setBatchActive}
                    disabled={!canEditBatchSettings}
                    data-testid="batch-active-switch"
                  />
                  <span className="text-sm text-complementary-text">
                    {batchActive ? t('form.batch_active_on') : t('form.batch_active_off')}
                  </span>
                </div>
              </div>
              {batchActive && (
                <div className="flex flex-col gap-1">
                  <Label htmlFor="batch-type" className="pb-2">
                    {t('form.batch_type_label')}
                  </Label>
                  <Select value={batchType} onValueChange={setBatchType} disabled={!canEditBatchSettings}>
                    <SelectTrigger id="batch-type" data-testid="batch-type-select">
                      <SelectValue placeholder={t('form.batch_type_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FIFO">FIFO</SelectItem>
                      <SelectItem value="LIFO">LIFO</SelectItem>
                      <SelectItem value="FEFO">FEFO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" />
          </div>
        ) : batches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-complementary-text">
            <p className="text-sm">{t('batches.empty')}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-complementary-text">{t('batches.count', { count: batches.length })}</span>
            </div>
            <div className="border border-card-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-card-border bg-card-bg">
                    <th className="text-left text-xs font-medium text-complementary-text uppercase py-3 px-4">
                      {tBatches('table.batch')}
                    </th>
                    <th className="text-left text-xs font-medium text-complementary-text uppercase py-3 px-4">
                      {tBatches('table.received_at')}
                    </th>
                    <th className="text-left text-xs font-medium text-complementary-text uppercase py-3 px-4">
                      {tBatches('table.expiry_date')}
                    </th>
                    <th className="text-right text-xs font-medium text-complementary-text uppercase py-3 px-4">
                      {tBatches('table.stock')}
                    </th>
                    <th className="text-center text-xs font-medium text-complementary-text uppercase py-3 px-4">
                      {tBatches('table.status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((batch) => {
                    const status = getBatchStatus(batch);
                    const styles = STATUS_STYLES[status] || STATUS_STYLES.active;
                    const totalStock = getBatchTotalStock(batch);

                    return (
                      <tr
                        key={batch.id}
                        className="border-b border-card-border/50 hover:bg-hover-press/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/batches/${batch.id}`)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex flex-col">
                            <span className="text-sm font-semibold text-text">{batch.batch_number}</span>
                            {batch.supplier_reference && (
                              <span className="text-xs text-complementary-text">{batch.supplier_reference}</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-text">{formatDate(batch.received_at)}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-text">{formatDate(batch.expiry_date)}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-sm font-medium text-text">{totalStock} uds.</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={`text-xs ${styles.bg} ${styles.text}`}>
                            {tBatches(`status.${status}`)}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
